import React, { useState } from "react";
import Drawer from "../../components/drawer/drawer";
import Modal from "../../components/modal/modal";
import PageHeader from "../../components/page-header/page-header";
import { usePagination } from "../../hooks/use-pagination";
import { Review } from "../../types";
import { ReviewForm, ReviewTable } from "./components";
import { useReviews } from "./hooks";
import { ReviewManagementState } from "./types/reviews-types";
import {
  resetReviewState,
  openEditDrawer,
  openCreateDrawer,
  closeDrawer,
  openDeleteModal,
  closeDeleteModal,
  getDrawerTitle,
} from "./utils/reviews-utils";
import "./review.css";
import { toast } from "sonner";

const ReviewPage: React.FC = () => {
  const [state, setState] = useState<ReviewManagementState>(resetReviewState());

  const {
    reviews,
    isLoading,
    handleCreateReview,
    handleUpdateReview,
    handleDeleteReview,
  } = useReviews();

  const {
    currentPage,
    itemsPerPage,
    paginatedItems,
    totalPages,
    handlePageChange,
    handleItemsPerPageChange,
  } = usePagination(reviews);

  // Event handlers
  const handleSubmit = async (values: Partial<Review>): Promise<void> => {
    let response: any;
    if (state.editingReview) {
      response = await handleUpdateReview(state.editingReview.id, values);
    } else {
      response = await handleCreateReview(values);
    }

    const hasSuccessField = typeof response?.success === "boolean";
    if (!hasSuccessField || response?.success) {
      toast.success(response?.message || "Операция выполнена успешно");
      setState(closeDrawer());
    } else {
      toast.error(response?.message || "Произошла ошибка");
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!state.deletingReview) return;

    const success = await handleDeleteReview(state.deletingReview.id);
    if (success) {
      setState(closeDeleteModal());
    }
  };

  const handleCreateDrawer = (): void => {
    setState(openCreateDrawer());
  };

  const handleEditDrawer = (review: Review): void => {
    setState(openEditDrawer(review));
  };

  const handleCloseDrawer = (): void => {
    setState(closeDrawer());
  };

  const handleDeleteModal = (review: Review): void => {
    setState(openDeleteModal(review));
  };

  const handleCloseDeleteModal = (): void => {
    setState(closeDeleteModal());
  };

  return (
    <div className="review">
      <PageHeader
        title="Отзывы"
        subtitle="Управление отзывами клиентов"
        actionButton={{
          text: "Добавить отзыв",
          icon: "plus",
          onClick: handleCreateDrawer,
        }}
      />

      <ReviewTable
        reviews={paginatedItems}
        isLoading={isLoading}
        onEdit={handleEditDrawer}
        onDelete={handleDeleteModal}
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
      />

      <Drawer
        title={getDrawerTitle(state.editingReview)}
        isOpen={state.isDrawerOpen}
        onClose={handleCloseDrawer}
        width={600}
      >
        <ReviewForm
          initialValues={state.editingReview}
          onSubmit={handleSubmit}
          onCancel={handleCloseDrawer}
          isLoading={isLoading}
        />
      </Drawer>

      <Modal
        isOpen={state.isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        title="Подтвердите удаление"
        onConfirm={handleDelete}
      >
        <p>Вы уверены, что хотите удалить этот отзыв?</p>
        <p>
          <strong>{state.deletingReview?.customerName}</strong>
        </p>
      </Modal>
    </div>
  );
};

export default ReviewPage;
